import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../config/supabase';
import DashboardHeader from '../components/DashboardHeader';
import DashboardSidebar from '../components/DashboardSidebar';
import WeekPicker from '../../standouts/components/WeekPicker';
import '../styles/SettingsScreen.css';

const LandingPageScreen = () => {
  const { getCurrentWeek, setCurrentWeek } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [locationText, setLocationText] = useState('');
  const [headingText, setHeadingText] = useState('');
  const [subheadingText, setSubheadingText] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Current week state
  const [weekNumber, setWeekNumber] = useState('');
  const [weekStartDate, setWeekStartDate] = useState('');
  const [weekDateRange, setWeekDateRange] = useState('');
  const [weekLoading, setWeekLoading] = useState(false);
  const [weekSuccess, setWeekSuccess] = useState('');
  const [weekError, setWeekError] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchCurrentWeek();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('landing_page_settings')
        .select('*')
        .eq('id', 1)
        .single();
      if (error) throw error;
      if (data) {
        setLocationText(data.location_text || '');
        setHeadingText(data.heading_text || '');
        setSubheadingText(data.subheading_text || '');
      }
    } catch (err) {
      console.error('Error fetching landing page settings:', err);
    } finally {
      setFetching(false);
    }
  };

  const fetchCurrentWeek = async () => {
    const result = await getCurrentWeek();
    if (result.success && result.data) {
      const label = result.data.current_week_label || '';
      setWeekNumber(label.replace(/[^0-9]/g, ''));
      setWeekDateRange(result.data.current_week_date_range || '');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!locationText.trim() || !headingText.trim() || !subheadingText.trim()) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase
        .from('landing_page_settings')
        .update({
          location_text: locationText.trim(),
          heading_text: headingText.trim(),
          subheading_text: subheadingText.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', 1);
      if (error) throw error;
      setSuccess('Landing page updated successfully!');
    } catch (err) {
      setError('Failed to update. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWeek = async (e) => {
    e.preventDefault();
    setWeekError('');
    setWeekSuccess('');
    if (!weekNumber.trim() && !weekDateRange.trim()) {
      setWeekError('Please enter at least a week number or date range.');
      return;
    }
    setWeekLoading(true);
    const label = weekNumber ? `Week ${weekNumber}` : '';
    const result = await setCurrentWeek(label, weekDateRange.trim());
    if (result.success) {
      setWeekSuccess('Current week updated! Standouts landing page will now default to this week.');
    } else {
      setWeekError('Failed to update. Please try again.');
    }
    setWeekLoading(false);
  };

  return (
    <div className="dashboard-layout">
      <DashboardSidebar
        activeItem="landing-page"
        setActiveItem={() => {}}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />

      <div className="dashboard-main">
        <div className="settings-container">

          <div className="settings-card">
            <div className="settings-card-header">
              <h2 className="settings-card-title">Landing Page Settings</h2>
              <p className="settings-card-subtitle">Update the header section of your landing page</p>
            </div>

            {fetching ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>
                Loading...
              </div>
            ) : (
              <form className="settings-form" onSubmit={handleSave}>
                <div className="settings-form-group">
                  <label className="settings-form-label">Location</label>
                  <input
                    type="text"
                    className="settings-form-input"
                    placeholder="e.g. Temecula Valley"
                    value={locationText}
                    onChange={(e) => setLocationText(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="settings-form-group">
                  <label className="settings-form-label">Heading</label>
                  <input
                    type="text"
                    className="settings-form-input"
                    placeholder="e.g. The Spotlight"
                    value={headingText}
                    onChange={(e) => setHeadingText(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="settings-form-group">
                  <label className="settings-form-label">Subheading</label>
                  <input
                    type="text"
                    className="settings-form-input"
                    placeholder="e.g. Where athletes prove it"
                    value={subheadingText}
                    onChange={(e) => setSubheadingText(e.target.value)}
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="settings-error">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                {success && (
                  <div className="settings-success">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <span>{success}</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="settings-submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageScreen;
